import React, { useState, useEffect, useRef } from "react";
import { FaPaperclip, FaTimesCircle, FaFile, FaReply } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatChatDate, formatChatTime, shouldShowDateSeparator } from "../../utils/dateUtils";
import "./Chatbox.css";
import io from "socket.io-client";

const EmployeeChatbox = ({ user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [userTyping, setUserTyping] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // New state for reply
  const [chatboxSize, setChatboxSize] = useState({ width: 380, height: 500 });
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const resizeRef = useRef({ isResizing: false, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });
  const textareaRef = useRef(null);
  const sendingLock = useRef(false);
  const messagesEndRef = useRef(null);

  // Resize functionality
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    const maxHeight = 120; // Same as maxHeight in style
    textarea.style.height = newHeight > maxHeight ? maxHeight + 'px' : newHeight + 'px';
    setNewMessage(textarea.value);
    
    // Send typing indicator to server
    if (socketRef.current) {
      socketRef.current.emit('typing', {
        userId: user._id, 
        isTyping: textarea.value.length > 0
      });
    }
  };

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!user) return;
    
    // Connect to Socket.IO server
    socketRef.current = io(process.env.REACT_APP_API_BASE_URL);
    
    // Join admin room
    socketRef.current.emit('joinRoom', 'admin');
    
    // Listen for new messages
    socketRef.current.on('newMessage', (message) => {
      if (message.userId === user._id) {
        setMessages(prevMessages => [...prevMessages, message]);
      }
    });
    
    // Listen for typing indicators
    socketRef.current.on('userTyping', ({ userId, isTyping }) => {
      if (userId === user._id) {
        setUserTyping(isTyping);
      }
    });
    
    // Clean up on unmount
    return () => {
      socketRef.current.disconnect();
    };
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!user) return;

    const fetchChatHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/history?userId=${user._id}`
        );
        if (!response.ok) throw new Error("Failed to fetch chat history.");
        const data = await response.json();
        setMessages(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchChatHistory();

  }, [user]);

  const handleReply = (message) => {
    setReplyingTo(message);
    setNewMessage(""); // Clear current message when starting reply
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleMessageSend = async () => {
    // Prevent multiple sends by checking lock
    if (sendingLock.current) return;
    
    if (newMessage.trim() !== "" || selectedFiles.length > 0) {
      // Set lock to prevent multiple sends
      sendingLock.current = true;
      setIsSending(true);
      setSendProgress(10); // Start progress
      
      try {
        if (selectedFiles.length > 0) {
          setSendProgress(30);
          await uploadFiles();
          setSendProgress(70);
          if (newMessage.trim() !== "") {
            await sendTextMessage();
          }
        } else {
          setSendProgress(50);
          await sendTextMessage();
        }
        setSendProgress(100);
        
        // Reset progress after completion
        setTimeout(() => {
          setIsSending(false);
          setSendProgress(0);
        }, 500);
      } catch (error) {
        setError("Failed to send message. Please try again.");
        setIsSending(false);
        setSendProgress(0);
      } finally {
        // Release lock after sending completes or fails
        sendingLock.current = false;
      }
    }
  };

  const sendTextMessage = async () => {
    const messageText = newMessage.trim();
    
    // Show message immediately with pending status
    const tempId = `temp-${Date.now()}`;
    const instantMessage = {
      _id: tempId,
      user: "Admin",
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      pending: true
    };
    
    // Add message to UI immediately
    setMessages(prevMessages => [...prevMessages, instantMessage]);
    
    const newMessageObj = {
      user: "Admin",
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      replyTo: replyingTo ? replyingTo._id : null, // Include replyTo if replying
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify(newMessageObj),
      });

      if (!response.ok) throw new Error("Failed to send message.");

      const data = await response.json();
      
      // Update the temporary message with the actual server data
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === tempId ? 
          { ...data, pending: false } : msg
        )
      );
      
      // Clear message input after successful send
      setNewMessage("");
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      
      // Let the user know admin is no longer typing
      if (socketRef.current) {
        socketRef.current.emit('typing', {
          userId: user._id,
          isTyping: false
        });
      }
    } catch (error) {
      // Mark the message as failed instead of removing it
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === tempId ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const removeSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem("adminToken");
    const formData = new FormData();

    // Add temporary file messages with pending status
    const tempMessages = selectedFiles.map(file => ({
      _id: `temp-file-${Date.now()}-${file.name}`,
      user: "Admin",
      message: `File uploaded: ${file.originalname || file.name}`,
      sender: "admin",
      timestamp: new Date().toISOString(),
      originalName: file.name,
      userId: user._id,
      pending: true
    }));
    
    // Show files as pending in UI
    setMessages(prevMessages => [...prevMessages, ...tempMessages]);

    selectedFiles.forEach(file => {
      formData.append("files", file);
    });

    formData.append("user", "Admin");
    formData.append("sender", "admin");
    formData.append("userId", user._id);
    formData.append("replyTo", replyingTo ? replyingTo._id : ""); // Include replyTo if replying

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload files.");
      }

      const data = await response.json();
      
      // Remove temporary messages and add actual ones
      setMessages(prevMessages => {
        const filteredMessages = prevMessages.filter(msg => !msg._id.startsWith('temp-file-'));
        return [...filteredMessages, ...data];
      });
      
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      // Mark temporary file messages as failed
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-file-') ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  const handleMouseDown = (e) => {
    resizeRef.current.isResizing = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startWidth = chatboxSize.width;
    resizeRef.current.startHeight = chatboxSize.height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!resizeRef.current.isResizing) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    // Invert deltaX to make left drag increase width and right drag decrease width
    const newWidth = Math.max(300, Math.min(800, resizeRef.current.startWidth - deltaX));
    // Invert deltaY to make up drag increase height and down drag decrease height
    const newHeight = Math.max(400, Math.min(800, resizeRef.current.startHeight - deltaY));

    setChatboxSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    resizeRef.current.isResizing = false;

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save size preference to localStorage
    localStorage.setItem('chatboxSize', JSON.stringify(chatboxSize));
  };

  // Load saved size preference on component mount
  useEffect(() => {
    const savedSize = localStorage.getItem('chatboxSize');
    if (savedSize) {
      try {
        const parsedSize = JSON.parse(savedSize);
        // Ensure constraints are respected
        const constrainedSize = {
          width: Math.max(300, Math.min(800, parsedSize.width || 380)),
          height: Math.max(400, Math.min(800, parsedSize.height || 500))
        };
        setChatboxSize(constrainedSize);
      } catch (error) {
        console.error('Error parsing saved chatbox size:', error);
      }
    }
  }, []);

  const truncateFileName = (name) => {
    if (!name) return "";
    return name.length > 20
      ? `${name.substring(0, 10)}...${name.substring(name.length - 7)}`
      : name;
  };

  return (
    <div className="employee-chatbox" style={{ width: chatboxSize.width, height: chatboxSize.height }}>
      <div className="employee-chatbox-header">
        <div className="employee-chatbox-user-info">
          <div className="employee-chatbox-user-avatar">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="employee-chatbox-user-details">
            <h5>Chat with {user.name}</h5>
            <small>Customer Support</small>
          </div>
        </div>
        <button onClick={onClose} className="employee-close-chatbox-btn">
          <FaTimesCircle />
        </button>
      </div>

      <div className="employee-chatbox-body">
        {error && <div className="employee-chat-error">{error}</div>}

        {messages.map((msg, index) => {
          const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);
          
          return (
            <React.Fragment key={index}>
              {showDateSeparator && (
                <div className="employee-chat-date-divider">
                  <span>{formatChatDate(msg.timestamp)}</span>
                </div>
              )}
              <div className={`employee-message-container ${msg.sender} ${msg.pending ? 'pending' : ''} ${msg.failed ? 'failed' : ''}`}>
                <div className={`employee-message ${msg.sender} ${msg.replyTo ? 'reply' : ''}`}>
                  <div className="employee-message-actions">
                    <button onClick={() => handleReply(msg)} className="employee-reply-btn" title="Reply to message">
                      <FaReply />
                    </button>
                  </div>
                  <div className="employee-message-content">
                    {msg.replyTo && msg.replyTo.user && msg.replyTo.message && (
                      <div className="reply-preview">
                        <small>Replying to {msg.replyTo.user}: {msg.replyTo.message?.substring(0, 50)}...</small>
                      </div>
                    )}
                    {msg.fileUrl ? (
                      <div className="employee-message-file">
                        <a
                          href={`${process.env.REACT_APP_API_BASE_URL}/uploads/chat/${msg.fileUrl.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="employee-file-link"
                        >
                          <div className="employee-file-preview">
                            <div className="employee-file-icon">
                              <FaFile />
                            </div>
                            <div className="employee-file-info">
                              <span className="employee-file-name">{truncateFileName(msg.originalName)}</span>
                              <span className="employee-file-size">Click to download</span>
                            </div>
                          </div>
                        </a>
                      </div>
                    ) : (
                      <p>{msg.message}</p>
                    )}
                    <div className="employee-message-meta">
                      <span className="employee-message-time">
                        {msg.pending ? 'Sending...' : msg.failed ? 'Failed to send' : formatChatTime(msg.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        
        {userTyping && (
          <div className="employee-typing-indicator">
            User is typing...
          </div>
        )}
        
        {isSending && (
          <div className="employee-send-progress-container">
            <div 
              className="employee-send-progress-bar" 
              style={{ width: `${sendProgress}%` }}
            ></div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="employee-chatbox-footer">
        {replyingTo && (
          <div className="employee-reply-preview-container">
            <div className="employee-reply-preview">
              <small>Replying to: {replyingTo.user} - {replyingTo.message?.substring(0, 100)}...</small>
              <button onClick={cancelReply} className="employee-cancel-reply-btn">
                <FaTimesCircle />
              </button>
            </div>
          </div>
        )}
        {selectedFiles.length > 0 && (
          <div className="employee-selected-files-container">
            <div className="employee-selected-files-header">
              <span>Files to send ({selectedFiles.length})</span>
            </div>
            <div className="employee-selected-files-list">
              {selectedFiles.map((file, index) => (
                <div key={index} className="employee-selected-file">
                  <div className="employee-selected-file-info">
                    <FaFile className="employee-selected-file-icon" />
                    <span className="employee-selected-file-name">{truncateFileName(file.name)}</span>
                  </div>
                  <button 
                    onClick={() => removeSelectedFile(index)} 
                    className="employee-remove-file-btn"
                    aria-label="Remove file"
                  >
                    <FaTimesCircle />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="employee-chatbox-input-container">
          <label htmlFor="employee-file-upload" className="employee-file-upload-label">
            <FaPaperclip />
            <input
              id="employee-file-upload"
              type="file"
              multiple
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: "none" }}
            />
          </label>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleTextareaChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            placeholder="Type your message..."
            className="employee-chatbox-input"
            rows="1"
            style={{ resize: 'none', overflow: 'auto', maxHeight: '120px' }}
          />
          <button
            onClick={handleMessageSend}
            className="employee-send-btn"
            disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)}
            aria-label="Send message"
          >
            <IoMdSend />
          </button>
        </div>
      </div>
      <div className="employee-chatbox-resize-handle" onMouseDown={handleMouseDown}></div>
    </div>
  );
};

export default EmployeeChatbox;